import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Lock, Loader2, Inbox, LogOut } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LABELS = [
    ["telephone", "Téléphone"],
    ["courriel", "Courriel"],
    ["objectif", "Objectif"],
    ["repas", "Repas/jour"],
    ["plats", "Plats préférés"],
    ["restrictions", "Restrictions"],
    ["message", "Message"],
];

const LeadCard = ({ l, i }) => (
    <div className="border border-white/10 bg-obsidian-surface p-6" data-testid={`admin-lead-${i}`}>
        <div className="flex flex-wrap items-baseline justify-between gap-2">
            <p className="font-heading text-xl font-bold tracking-tight">
                {l.prenom} {l.nom}
            </p>
            <p className="text-xs text-sand">
                {l.created_at ? new Date(l.created_at).toLocaleString("fr-CA") : ""}
            </p>
        </div>
        <dl className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2 text-sm">
            {LABELS.map(([k, label]) =>
                l[k] ? (
                    <div key={k} className={k === "plats" || k === "message" ? "sm:col-span-2" : ""}>
                        <dt className="text-sand text-xs uppercase tracking-wider">{label}</dt>
                        <dd className="text-cream/90 mt-0.5">{l[k]}</dd>
                    </div>
                ) : null
            )}
        </dl>
    </div>
);

const Admin = () => {
    const location = useLocation();
    const [user, setUser] = useState(location.state?.user || null);
    const [checked, setChecked] = useState(!!location.state?.user);
    const [leads, setLeads] = useState(null);
    const [key, setKey] = useState("");
    const [accessKey, setAccessKey] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(false);

    const ebookHref = user
        ? `${API}/ebook/pdf`
        : `${API}/ebook/pdf?key=${encodeURIComponent(accessKey || "")}`;

    useEffect(() => {
        if (user) return;
        fetch(`${API}/auth/me`, { credentials: "include" })
            .then((r) => (r.ok ? r.json() : null))
            .then((u) => {
                if (u) setUser(u);
                setChecked(true);
            })
            .catch(() => setChecked(true));
    }, [user]);

    useEffect(() => {
        if (!user) return;
        fetch(`${API}/leads`, { credentials: "include" })
            .then((r) => (r.ok ? r.json() : []))
            .then(setLeads)
            .catch(() => setLeads([]));
    }, [user]);

    const loginWithGoogle = () => {
        // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
        const redirectUrl = window.location.origin + "/admin";
        window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    };

    const loginWithKey = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(false);
        try {
            const r = await fetch(`${API}/leads?key=${encodeURIComponent(key)}`);
            if (!r.ok) throw new Error();
            setLeads(await r.json());
            setAccessKey(key);
        } catch {
            setError(true);
        }
        setLoading(false);
    };

    const logout = async () => {
        await fetch(`${API}/auth/logout`, { method: "POST", credentials: "include" });
        setUser(null);
        setLeads(null);
    };

    const authenticated = !!user || leads !== null;

    return (
        <div className="pt-28 md:pt-36 pb-24 min-h-screen" data-testid="admin-page">
            <div className="max-w-4xl mx-auto px-6 md:px-12">
                <div className="flex flex-wrap items-center justify-between gap-4">
                    <h1 className="font-heading text-3xl md:text-4xl font-bold tracking-tight">Demandes reçues</h1>
                    {user && (
                        <div className="flex items-center gap-3">
                            {user.picture && <img src={user.picture} alt="" className="h-8 w-8 rounded-full" />}
                            <span className="text-sm text-sand">{user.name || user.email}</span>
                            <button
                                onClick={logout}
                                data-testid="admin-logout-button"
                                className="inline-flex items-center gap-2 rounded-full border border-white/20 px-4 py-2 text-xs font-bold uppercase tracking-wider text-sand hover:text-cream hover:border-cream transition-colors"
                            >
                                <LogOut className="h-3.5 w-3.5" /> Déconnexion
                            </button>
                        </div>
                    )}
                </div>

                {authenticated && (
                    <div className="mt-4 flex flex-wrap gap-3">
                        <a
                            href={ebookHref}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="admin-ebook-download"
                            className="inline-flex items-center gap-2 rounded-full border border-ochre/50 px-5 py-2.5 text-sm font-bold uppercase tracking-wider text-ochre hover:bg-ochre hover:text-obsidian transition-colors duration-300"
                        >
                            Télécharger l'Ebook (PDF)
                        </a>
                        <a
                            href="/qr/qr-site-coach-romeo.png"
                            download
                            data-testid="admin-qr-site"
                            className="inline-flex items-center gap-2 rounded-full border border-white/25 px-5 py-2.5 text-sm font-bold uppercase tracking-wider text-cream hover:border-ochre hover:text-ochre transition-colors duration-300"
                        >
                            QR code — Site
                        </a>
                        <a
                            href="/qr/qr-whatsapp-coach-romeo.png"
                            download
                            data-testid="admin-qr-whatsapp"
                            className="inline-flex items-center gap-2 rounded-full border border-whatsapp/50 px-5 py-2.5 text-sm font-bold uppercase tracking-wider text-whatsapp hover:bg-whatsapp hover:text-white transition-colors duration-300"
                        >
                            QR code — WhatsApp
                        </a>
                        <a
                            href="/qr/qr-coachromeo-fonce.png"
                            download
                            data-testid="admin-qr-transparent"
                            className="inline-flex items-center gap-2 rounded-full border border-white/25 px-5 py-2.5 text-sm font-bold uppercase tracking-wider text-cream hover:border-ochre hover:text-ochre transition-colors duration-300"
                        >
                            QR photo (transparent)
                        </a>
                    </div>
                )}

                {!checked ? (
                    <p className="mt-10 flex items-center gap-3 text-sand">
                        <Loader2 className="h-5 w-5 animate-spin" /> Vérification de la session…
                    </p>
                ) : !authenticated ? (
                    <div className="mt-10 max-w-lg space-y-8">
                        <button
                            onClick={loginWithGoogle}
                            data-testid="admin-google-login"
                            className="w-full inline-flex items-center justify-center gap-3 rounded-full bg-cream px-6 py-4 text-sm font-bold uppercase tracking-wider text-obsidian transition-opacity hover:opacity-90"
                        >
                            <svg className="h-5 w-5" viewBox="0 0 24 24">
                                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1z"/>
                                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                                <path fill="#FBBC05" d="M5.84 14.1a7.16 7.16 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84z"/>
                                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z"/>
                            </svg>
                            Continuer avec Google
                        </button>
                        <div className="flex items-center gap-4 text-xs uppercase tracking-widest text-sand">
                            <span className="h-px flex-1 bg-white/10" /> ou avec la clé <span className="h-px flex-1 bg-white/10" />
                        </div>
                        <form onSubmit={loginWithKey} className="flex flex-col sm:flex-row gap-4">
                            <input
                                type="password"
                                data-testid="admin-key-input"
                                value={key}
                                onChange={(e) => setKey(e.target.value)}
                                placeholder="Clé d'accès"
                                className="flex-1 rounded-md border border-white/15 bg-obsidian px-4 py-3 text-cream focus:border-terracotta focus:outline-none"
                            />
                            <button
                                type="submit"
                                data-testid="admin-login-button"
                                disabled={loading}
                                className="inline-flex items-center justify-center gap-2 rounded-full bg-terracotta px-6 py-3 text-sm font-bold uppercase tracking-wider text-white hover:bg-terracotta-hover disabled:opacity-40"
                            >
                                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Lock className="h-4 w-4" />}
                                Voir les demandes
                            </button>
                        </form>
                        {error && (
                            <p className="text-sm text-terracotta" data-testid="admin-error">
                                Clé incorrecte ou erreur de connexion.
                            </p>
                        )}
                    </div>
                ) : leads === null ? (
                    <p className="mt-10 flex items-center gap-3 text-sand">
                        <Loader2 className="h-5 w-5 animate-spin" /> Chargement des demandes…
                    </p>
                ) : leads.length === 0 ? (
                    <p className="mt-10 flex items-center gap-3 text-sand" data-testid="admin-empty">
                        <Inbox className="h-5 w-5" /> Aucune demande pour le moment.
                    </p>
                ) : (
                    <div className="mt-10 space-y-5" data-testid="admin-leads-list">
                        {leads.map((l, i) => (
                            <LeadCard key={l.id || i} l={l} i={i} />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default Admin;
